import { useAssistantState } from "@assistant-ui/react";
import {
  Box,
  Flex,
  FormControl,
  FormLabel,
  Icon,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  useDisclosure,
  useToast,
} from "@chakra-ui/react";
import { toPng } from "html-to-image";
import React, {
  ReactElement,
  useCallback,
  useLayoutEffect,
  useState,
} from "react";
import { HiOutlineClipboard, HiOutlineEye } from "react-icons/hi2";
import { useNavigate } from "react-router-dom";

import { Button, NewReportIcon } from "../../../../components";
import { useCreateCustomInsightsReportMutation } from "../../../graphql";
import { useCustomInsightsChat } from "./CustomInsightsChatProvider";
import {
  type ThreadMessage,
  buildSaveReportVariables,
} from "./customInsightsChatUtils";
import { InsightsFeedback } from "./InsightsFeedback";
import { useSavedReports } from "./SavedReportsContext";

async function buildRichClipboardContent(
  contentEl: HTMLElement
): Promise<{ html: string; plain: string }> {
  const charts = Array.from(
    contentEl.querySelectorAll<HTMLElement>('[data-copy-role="chart"]')
  );

  const chartImages: Map<HTMLElement, string> = new Map();
  await Promise.all(
    charts.map(async (chart) => {
      try {
        const dataUrl = await toPng(chart, { pixelRatio: 2, skipFonts: true });
        chartImages.set(chart, dataUrl);
      } catch {
        // skip charts that fail to render
      }
    })
  );

  const clone = contentEl.cloneNode(true) as HTMLElement;

  const origCharts = Array.from(
    contentEl.querySelectorAll<HTMLElement>('[data-copy-role="chart"]')
  );
  const cloneCharts = Array.from(
    clone.querySelectorAll<HTMLElement>('[data-copy-role="chart"]')
  );
  origCharts.forEach((orig, i) => {
    const dataUrl = chartImages.get(orig);
    if (dataUrl && cloneCharts[i]) {
      const img = document.createElement("img");
      img.src = dataUrl;
      img.style.maxWidth = "100%";
      img.style.display = "block";
      img.style.margin = "8px 0";
      const { width, height } = orig.getBoundingClientRect();
      img.width = Math.round(width);
      img.height = Math.round(height);
      cloneCharts[i].replaceWith(img);
    }
  });

  [
    '[data-copy-role="chart-button"]',
    '[data-copy-role="reasoning"]',
    '[data-copy-role="tool-status"]',
    '[data-copy-role="sparkle-icon"]',
  ].forEach((sel) => {
    clone.querySelectorAll(sel).forEach((el) => el.remove());
  });

  clone
    .querySelectorAll<HTMLElement>('[data-copy-role="text-bubble"]')
    .forEach((el) => {
      el.setAttribute(
        "style",
        "background:none;padding:0;margin:0;border-radius:0"
      );
    });

  return { html: clone.innerHTML, plain: clone.textContent ?? "" };
}

export function ResultActions({
  messageId,
  reportId,
  contentRef,
}: {
  messageId?: string;
  reportId?: string;
  contentRef?: React.RefObject<HTMLDivElement>;
}): ReactElement | null {
  const navigate = useNavigate();
  const {
    messageIdToSavedReport,
    markMessageSaved,
    filterHistory,
    messageIdToFilterTimestamp,
  } = useCustomInsightsChat();
  const { addSavedReport } = useSavedReports();
  const toast = useToast();
  const {
    isOpen: isSaveOpen,
    onOpen: onOpenSave,
    onClose: onCloseSave,
  } = useDisclosure();
  const [reportTitle, setReportTitle] = useState("");
  const [createReport, { loading: saving }] =
    useCreateCustomInsightsReportMutation();

  const [copying, setCopying] = useState(false);
  const [hasCopyContentEl, setHasCopyContentEl] = useState(false);
  useLayoutEffect(() => {
    setHasCopyContentEl(!!contentRef?.current);
  }, [contentRef, messageId, reportId]);

  const messages = useAssistantState((s) => s.thread.messages) as
    | ThreadMessage[]
    | undefined;
  const messageList = messages ?? [];

  const handleCopyToClipboard = useCallback(async () => {
    if (!contentRef?.current) return;
    setCopying(true);
    try {
      const { html, plain } = await buildRichClipboardContent(
        contentRef.current
      );
      await navigator.clipboard.write([
        new ClipboardItem({
          "text/html": new Blob([html], { type: "text/html" }),
          "text/plain": new Blob([plain], { type: "text/plain" }),
        }),
      ]);
      toast({
        title: "Copied to clipboard",
        status: "success",
        duration: 2000,
      });
    } catch {
      toast({
        title: "Failed to copy",
        status: "error",
        duration: 2000,
      });
    } finally {
      setCopying(false);
    }
  }, [contentRef, toast]);

  let savedReportId = "";
  if (messageId) {
    savedReportId = messageIdToSavedReport[messageId] ?? "";
  } else if (reportId) {
    savedReportId = reportId;
  } else {
    return null;
  }

  const handleSave = async (): Promise<void> => {
    if (!messageId || !reportTitle.trim()) return;
    const variables = buildSaveReportVariables({
      messageId,
      title: reportTitle,
      messages: messageList,
      filterHistory,
      messageIdToFilterTimestamp,
    });
    try {
      const { data: mutationData } = await createReport({ variables });
      const mutationReport =
        mutationData?.createCustomInsightsReport?.customInsightsReport;
      onCloseSave();
      setReportTitle("");
      if (mutationReport) {
        addSavedReport(mutationReport);
        markMessageSaved(messageId, mutationReport.id);
      }
    } catch {
      toast({
        title: "Failed to save report",
        status: "error",
        duration: 3000,
      });
    }
  };

  return (
    <>
      <Flex gap="6px" width="100%" align="center">
        {messageId && <InsightsFeedback messageId={messageId} />}
        {hasCopyContentEl && (
          <Button
            size="sm"
            variant="outline"
            colorScheme="purple"
            isLoading={copying}
            onClick={handleCopyToClipboard}
            flex={1}
          >
            <Icon as={HiOutlineClipboard} boxSize={5} flexShrink={0} />
            <Box as="span" flex={1} textAlign="center">
              Copy to clipboard
            </Box>
          </Button>
        )}
        {messageId && !savedReportId && (
          <Button
            size="sm"
            variant="outline"
            colorScheme="purple"
            onClick={onOpenSave}
            flex={1}
          >
            <NewReportIcon boxSize={5} flexShrink={0} />
            <Box as="span" flex={1} textAlign="center">
              Save as report
            </Box>
          </Button>
        )}
        {savedReportId && (
          <Button
            size="sm"
            variant="outline"
            colorScheme="purple"
            fontWeight="normal"
            onClick={() =>
              navigate(`/insights/custom/reports/${savedReportId}`)
            }
            flex={1}
          >
            <Icon as={HiOutlineEye} boxSize={5} flexShrink={0} />
            <Box as="span" flex={1} textAlign="center">
              View Report
            </Box>
          </Button>
        )}
      </Flex>
      {messageId && (
        <Modal isOpen={isSaveOpen} onClose={onCloseSave} isCentered>
          <ModalOverlay />
          <ModalContent>
            <ModalHeader>Save Report</ModalHeader>
            <ModalCloseButton />
            <ModalBody>
              <FormControl>
                <FormLabel>Report name</FormLabel>
                <Input
                  value={reportTitle}
                  onChange={(e) => setReportTitle(e.target.value)}
                  placeholder="Enter a name for this report"
                  autoFocus
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      handleSave();
                    }
                  }}
                />
              </FormControl>
            </ModalBody>
            <ModalFooter gap={2}>
              <Button size="sm" variant="outline" onClick={onCloseSave}>
                Cancel
              </Button>
              <Button
                size="sm"
                variant="solid"
                isDisabled={!reportTitle.trim() || saving}
                isLoading={saving}
                onClick={handleSave}
              >
                Save
              </Button>
            </ModalFooter>
          </ModalContent>
        </Modal>
      )}
    </>
  );
}
